import React from 'react'
import { Route } from 'react-router'

// Constants
import { routes } from '@sketch/constants'

// Components
import { MountHandle } from '@sketch/utils'
import Section from 'components/Section'
import WorkspaceList from '../../components/WorkspaceList'
import SettingsForm from '../../components/SettingsForm'
import TwoFactorAuthenticationPanel from 'modules/user/components/TwoFactorAuthenticationPanel'
import SessionsManagementPanel from 'modules/user/components/SessionsManagementPanel'
import { LinkButton } from '@sketch/components'
import { EmailNotificationsPanel } from '../../components/EmailNotificationsPanel'
import { handleFetchMore } from 'components/InfiniteList'
import { OtherOptionsPanel } from 'modules/user/components/OtherOptionsPanel'

// Modals
import LeaveWorkspaceModal from '../../modals/LeaveWorkspaceModal'

// Hooks
import { useFlag } from 'containers/flags'
import useUserProfile from 'modules/user/useUserProfile'
import { useUserAuthorizations } from 'modules/sso/operations'
import { useToast } from '@sketch/toasts'
import { useModalContext } from 'containers/ModalManager'
import {
  getPersonalAuthorization,
  isSsoAuthorization,
} from 'graphql/multisession'

// Types
import {
  useGetUserSettingsQuery,
  useUserUpdateMutation,
  InitialUserFragment,
} from '@sketch/gql-types'

// Helpers
const createOptimisticResponse = (
  user: InitialUserFragment,
  receiveEmailNotifications: boolean
) => ({
  __typename: 'RootMutationType' as const,
  userUpdate: {
    __typename: 'UserUpdatePayload' as const,
    errors: [],
    successful: true,
    user: {
      ...user,
      receiveEmailNotifications,
    },
  },
})

const GeneralSettings: React.FC = () => {
  const { showModal } = useModalContext()
  const { showToast } = useToast()
  const { data: userData } = useUserProfile()
  const { authorizations, hasPersonalAuthorization } = useUserAuthorizations()
  const isSessionsManagementOn = useFlag('sessions-management')

  const [updateUser, { called }] = useUserUpdateMutation({
    redirectErrors: false,
    onCompleted: data => {
      const { errors, successful } = data.userUpdate || {}
      if (successful) {
        showToast('Profile settings updated')
      }

      if (errors?.length! > 0) {
        const errorMessage = errors!.map(error => error?.message).join('; ')
        showToast(errorMessage, 'negative')
      }
    },
    onError: error => {
      showToast(error.message, 'negative')
    },
  })

  const { data: userSettingsData, fetchMore } = useGetUserSettingsQuery()

  const handleLoadMore = handleFetchMore(
    fetchMore,
    ['me', 'workspaceMemberships', 'entries'],
    { after: userSettingsData?.me?.workspaceMemberships?.meta.after }
  )

  // Merge the user objects, more precisely get the "newEmail".
  const user = {
    ...userData!.me!,
    ...userSettingsData?.me!,
  }

  const memberships = userSettingsData?.me?.workspaceMemberships?.entries || []

  const isSignedInWithSso = authorizations.some(isSsoAuthorization)

  const isSsoOnly = !getPersonalAuthorization()

  return (
    <>
      <Route
        path={routes.PERSONAL_SETTINGS_UNSUBSCRIBE.template()}
        render={({ history }) => (
          <MountHandle
            onMount={async () => {
              // Prevent calling the mutation again if it's already called (when mounting)
              // Related to https://github.com/sketch-hq/Cloud/issues/6102#issuecomment-804823199
              if (called) {
                return
              }

              user.hasPersonalIdentity &&
                (await updateUser({
                  variables: { input: { receiveEmailNotifications: false } },
                  optimisticResponse: createOptimisticResponse(user, false),
                }))

              history.replace(routes.PERSONAL_SETTINGS.create({}))
            }}
          />
        )}
      />
      <Section title="Personal">
        <SettingsForm
          userName={user.name!}
          userEmail={user.email!}
          userNewEmail={user.newEmail}
          userAvatarSrc={user.avatar?.large || user.avatar!.small!}
          userHasAvatar={user.hasAvatar!}
          userHasPersonalIdentity={user.hasPersonalIdentity}
          userHasPersonalAuthorization={hasPersonalAuthorization}
          isSignedInWithSso={isSignedInWithSso}
          onUserUpdate={updateUser}
        />
      </Section>

      {/** 2FA Panel */}
      {!isSsoOnly && (
        <TwoFactorAuthenticationPanel
          mfaState={user.mfaState}
          isVerified={user.isVerified}
        />
      )}

      {memberships.length > 0 && (
        <WorkspaceList
          memberships={memberships}
          onLoadMore={handleLoadMore}
          onLeaveWorkspace={membership => {
            showModal(LeaveWorkspaceModal, {
              membershipId: membership.identifier,
              workspace: membership.workspace,
            })
          }}
          linkButton={
            hasPersonalAuthorization && (
              <LinkButton
                to={routes.WORKSPACE_CREATE.create({})}
                variant="primary"
                size="40"
              >
                Create Workspace&hellip;
              </LinkButton>
            )
          }
        />
      )}

      {/* Sessions Management Panel */}
      {isSessionsManagementOn && <SessionsManagementPanel />}

      {user.hasPersonalIdentity && (
        <EmailNotificationsPanel
          receiveEmailNotifications={user.receiveEmailNotifications!}
        />
      )}

      <OtherOptionsPanel hasPersonalIdentity={user.hasPersonalIdentity} />
    </>
  )
}

export default GeneralSettings
